/* eslint-disable react/forbid-prop-types */
import { Button, Grid, Icon, IconButton, Popover, Tooltip } from '@mui/material'
import Checkbox from 'components/forms/Checkbox'
import Input from 'components/forms/Input'
import RadioButtons from 'components/forms/RadioButtons'
import Select from 'components/forms/Select'
import { StyledFieldSet, StyledLegend } from 'components/StyledElements'
import React, { useMemo, useRef, useState } from 'react'
import i18n from 'simple-react-i18n'
import useBoolean from 'utils/customHook/useBoolean'
import PropTypes from 'prop-types'
import {
    AUTO,
    ALL,
    CUMUL,
    DAY,
    HOUR,
    CUMUL_PERSO_MAX,
    CUMUL_PERSO_MIN,
    CUMUL_PERSO_SUM,
    CUMUL_PERSO_AVERAGE,
} from 'hydrometry/constants/HydroOptionConstant'

const HydroCumulOption = ({
    defaultGroupType = AUTO,
    onChangeGroupType = () => { },
    disabled = false,
}) => {
    const {
        defaultOptionTime,
        defaultNbTime,
        defaultCumul,
    } = useMemo(() => {
        if (!defaultGroupType.startsWith(CUMUL)) {
            return {}
        }
        const [c, p, o, nbHourString] = defaultGroupType.split('_')
        const nbHour = parseInt(nbHourString)
        if (isNaN(nbHour)) {
            return {}
        }
        const isDay = nbHour % 24 === 0
        return {
            defaultOptionTime: isDay ? DAY : HOUR,
            defaultNbTime: isDay ? nbHour / 24 : nbHour,
            defaultCumul: `${c}_${p}_${o}`,
        }
    }, [defaultGroupType])

    const [groupType, setGroupType] = useState(defaultGroupType)
    const [optionTime, setOptionTime] = useState(defaultOptionTime)
    const [nbTime, setNbTime] = useState(defaultNbTime)
    const [cumul, setCumul] = useState(defaultCumul)


    const optionDayHour = useMemo(() => [
        { value: HOUR, label: i18n.hour },
        { value: DAY, label: i18n.day },
    ], [])

    const optionCumul = useMemo(() => [
        { value: CUMUL_PERSO_MAX, label: i18n.max },
        { value: CUMUL_PERSO_MIN, label: i18n.min },
        { value: CUMUL_PERSO_SUM, label: i18n.sum },
        { value: CUMUL_PERSO_AVERAGE, label: i18n.average },
    ], [])

    const checkCumul = groupType.startsWith(CUMUL)

    return (
        <Grid container style={{ width: '350px', padding: '5' }}>
            <Grid size={12}>
                <h5 className='center'>{i18n.regrouping}</h5>
            </Grid>
            <Grid container size={12}>
                <Grid size='auto'>
                    <Checkbox
                        checked={groupType === AUTO}
                        label={i18n.auto}
                        disabled={disabled}
                        onChange={() => {
                            onChangeGroupType(AUTO)
                            setGroupType(AUTO)
                        }}
                    />
                </Grid>
                <Grid size='auto'>
                    <Tooltip
                        title={(
                            <span style={{ whiteSpace: 'pre-wrap' }}>
                                {i18n.descriptionCumulAuto}
                            </span>
                        )}
                    >
                        <Icon sx={{ fontSize: 18, color: 'grey' }}>info</Icon>
                    </Tooltip>
                </Grid>
            </Grid>
            <Grid size={12}>
                <Checkbox
                    checked={groupType === ALL}
                    label={i18n.allMeasures}
                    disabled={disabled}
                    onChange={() => {
                        onChangeGroupType(ALL)
                        setGroupType(ALL)
                    }}
                />
            </Grid>

            <Grid size={12}>
                <Checkbox
                    checked={checkCumul}
                    label={i18n.personalizedGrouping}
                    disabled={disabled}
                    onChange={() => setGroupType(CUMUL)}
                />
            </Grid>
            <Grid size={12}>
                <StyledFieldSet>
                    <StyledLegend>{i18n.personalizedAccumulation}</StyledLegend>
                    <Grid container rowSpacing={1}>
                        <Grid size={12}>
                            <RadioButtons
                                colOption={6}
                                label={i18n.modeDaysHours}
                                elements={optionDayHour}
                                selected={optionTime}
                                onChange={setOptionTime}
                                disabled={!checkCumul || disabled}
                            />
                        </Grid>
                        <Grid size={12}>
                            <Select
                                label={i18n.modeCumul}
                                options={optionCumul}
                                value={cumul}
                                onChange={setCumul}
                                disabled={!checkCumul || disabled}
                            />
                        </Grid>
                        <Grid size={12}>
                            <Input
                                title={i18n.numberHoursDays}
                                value={nbTime}
                                onChange={setNbTime}
                                disabled={!checkCumul || disabled}

                            />
                        </Grid>
                        <Grid size={12} align='center'>
                            <Button
                                onClick={() => {
                                    const hydroGrouped = `${cumul}_${optionTime === DAY ? nbTime * 24 : nbTime}`
                                    onChangeGroupType(hydroGrouped)
                                }}
                                variant='outlined'
                                disabled={!checkCumul || disabled}
                            >
                                {i18n.calculate}
                            </Button>
                        </Grid>
                    </Grid>
                </StyledFieldSet>
            </Grid>
        </Grid>
    )
}

HydroCumulOption.propTypes = {
    defaultGroupType: PropTypes.string,
    onChangeGroupType: PropTypes.func,
    disabled: PropTypes.bool,
}

const HydroCumulOptionIcon = ({
    defaultGroupType,
    onChangeGroupType,
    color = '#161832',
    disabled = false,
    'data-cy': dataCy,
}) => {
    const {
        value: isHydroCumulOptionOpen,
        toggle: toggleHydroCumulOption,
    } = useBoolean(false)
    const anchorEl = useRef()

    return (
        <>
            <Tooltip title={i18n.hydrometricGrouping}>
                <IconButton
                    sx={{ fontSize: '32px' }}
                    onClick={toggleHydroCumulOption}
                    data-cy={dataCy}
                    ref={anchorEl}
                >
                    <Icon fontSize='inherit' sx={{ color }}>settings</Icon>
                </IconButton>
            </Tooltip>
            <Popover
                open={isHydroCumulOptionOpen}
                anchorEl={anchorEl.current}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                onClose={toggleHydroCumulOption}
            >
                <HydroCumulOption
                    defaultGroupType={defaultGroupType}
                    onChangeGroupType={onChangeGroupType}
                    disabled={disabled}
                />
            </Popover>
        </>
    )
}

HydroCumulOptionIcon.propTypes = {
    defaultGroupType: PropTypes.string,
    onChangeGroupType: PropTypes.func,
    color: PropTypes.string,
    disabled: PropTypes.bool,
    'data-cy': PropTypes.string,
}

export { HydroCumulOptionIcon, HydroCumulOption }